/* Patriot Bikes V2.0 — header behaviour (mobile drawer, accordions, announcement).
   Progressive enhancement: desktop mega-menus are CSS-only; this adds the
   mobile drawer and dismissible announcement. */
class PBHeader extends HTMLElement {
  constructor() {
    super();
    this.onKeydown = this.onKeydown.bind(this);
    this.closeMegaMenus = this.closeMegaMenus.bind(this);
  }

  connectedCallback() {
    this.drawer = this.querySelector('.pb-drawer');
    this.backdrop = this.querySelector('.pb-drawer-backdrop');
    this.openBtn = this.querySelector('[data-pb-drawer-open]');
    this.closeBtn = this.querySelector('[data-pb-drawer-close]');

    if (this.openBtn) this.openBtn.addEventListener('click', () => this.openDrawer());
    if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.closeDrawer());
    if (this.backdrop) this.backdrop.addEventListener('click', () => this.closeDrawer());

    this.megaItems = Array.from(this.querySelectorAll('.pb-nav__li')).filter((item) =>
      item.querySelector('.pb-mega')
    );
    this.megaItems.forEach((item) => {
      const link = item.querySelector('.pb-nav__link');
      const open = () => this.openMegaMenu(item);
      const close = () => this.queueMegaClose(item);

      if (link) link.setAttribute('aria-expanded', 'false');
      item.addEventListener('pointerenter', open);
      item.addEventListener('pointerleave', close);
      item.addEventListener('focusin', open);
      item.addEventListener('focusout', (event) => {
        if (!item.contains(event.relatedTarget)) close();
      });
    });

    // Keep mega panels inside the viewport (a wide centred panel under a
    // right-side item otherwise overflows the right edge → horizontal scroll).
    // Re-run after fonts/layout settle: the webfont swap changes nav widths and
    // shifts each centred panel, so a single early pass isn't enough.
    this.clampMegas();
    this._onResize = () => this.clampMegas();
    window.addEventListener('resize', this._onResize);
    window.addEventListener('load', this._onResize);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => this.clampMegas());
    requestAnimationFrame(() => this.clampMegas());

    // Drawer accordions
    this.querySelectorAll('[data-pb-accordion]').forEach((btn) => {
      btn.addEventListener('click', () => this.toggleAccordion(btn));
    });

    // Announcement dismiss
    const dismiss = this.querySelector('[data-pb-announcement-close]');
    const announcement = this.querySelector('.pb-announcement');
    if (dismiss && announcement) {
      dismiss.addEventListener('click', () => { announcement.hidden = true; });
    }
  }

  clampMegas() {
    const gutter = 12;
    const vw = document.documentElement.clientWidth; // excludes scrollbar
    this.megaItems.forEach((item) => {
      const mega = item.querySelector('.pb-mega');
      if (!mega) return;
      mega.style.marginLeft = '0px'; // reset before measuring
      const r = mega.getBoundingClientRect();
      let shift = 0;
      if (r.right > vw - gutter) shift = -(r.right - (vw - gutter));
      if (r.left + shift < gutter) shift = gutter - r.left; // don't push past the left edge
      mega.style.marginLeft = shift ? `${Math.round(shift)}px` : '';
    });
  }

  openDrawer() {
    if (!this.drawer) return;
    this.drawer.classList.add('is-open');
    if (this.backdrop) this.backdrop.classList.add('is-open');
    this.drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('pb-no-scroll');
    document.addEventListener('keydown', this.onKeydown);
    if (this.closeBtn) this.closeBtn.focus();
  }

  closeDrawer() {
    if (!this.drawer) return;
    this.drawer.classList.remove('is-open');
    if (this.backdrop) this.backdrop.classList.remove('is-open');
    this.drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('pb-no-scroll');
    document.removeEventListener('keydown', this.onKeydown);
    if (this.openBtn) this.openBtn.focus();
  }

  onKeydown(e) {
    if (e.key === 'Escape') {
      this.closeDrawer();
      this.closeMegaMenus();
    }
  }

  openMegaMenu(item) {
    if (!item) return;
    clearTimeout(item.pbMegaTimer);
    this.megaItems.forEach((candidate) => {
      if (candidate !== item) this.closeMegaMenu(candidate);
    });
    item.classList.add('is-open');
    const link = item.querySelector('.pb-nav__link');
    if (link) link.setAttribute('aria-expanded', 'true');
  }

  queueMegaClose(item) {
    if (!item) return;
    clearTimeout(item.pbMegaTimer);
    item.pbMegaTimer = setTimeout(() => this.closeMegaMenu(item), 120);
  }

  closeMegaMenu(item) {
    if (!item) return;
    clearTimeout(item.pbMegaTimer);
    item.classList.remove('is-open');
    const link = item.querySelector('.pb-nav__link');
    if (link) link.setAttribute('aria-expanded', 'false');
  }

  closeMegaMenus() {
    if (!this.megaItems) return;
    this.megaItems.forEach((item) => this.closeMegaMenu(item));
  }

  toggleAccordion(btn) {
    const item = btn.closest('.pb-drawer__item');
    if (!item) return;
    const isOpen = item.classList.toggle('is-open');
    btn.setAttribute('aria-expanded', String(isOpen));
  }
}

if (!customElements.get('pb-header')) {
  customElements.define('pb-header', PBHeader);
}
